import { MediaStoryCard } from '@components/display/cards';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, Grid2, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useGetOverviewVideosQuery } from '@overview/services';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

const ITEMS_PER_PAGE_MD = 4;
const ITEMS_PER_PAGE_SM = 3;

export const OverviewVideos = () => {
    const [openVideo, setOpenVideo] = useState<string | null>(null);

    const theme = useTheme();
    const intl = useIntl();

    const isMdOrAbove = useMediaQuery(theme.breakpoints.up('md'));
    const { data: latestVideos, isLoading: isOverviewVideosLoading } = useGetOverviewVideosQuery({
        page: 1,
        page_size: isMdOrAbove ? ITEMS_PER_PAGE_MD : ITEMS_PER_PAGE_SM,
    });

    return !!latestVideos?.count || isOverviewVideosLoading ? (
        <Box component="section" px={{ xs: '20px', md: '65px' }} py="25px">
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb="26px">
                <Typography fontFamily="museo-sans" fontSize={32} fontWeight={600} letterSpacing={-1}>
                    <FormattedMessage id="videos.latest" defaultMessage="Latest Videos" />
                </Typography>
                <Button
                    component={Link}
                    to="videos"
                    variant="text"
                    endIcon={<ArrowForwardIcon />}
                    className="min-w-fit font-medium"
                >
                    <FormattedMessage id="common.actions.showAll" defaultMessage="Show all" />
                </Button>
            </Stack>
            <Grid2 container spacing="32px" justifyContent={isOverviewVideosLoading ? 'space-between' : undefined}>
                {isOverviewVideosLoading
                    ? Array.from({ length: isMdOrAbove ? ITEMS_PER_PAGE_MD : ITEMS_PER_PAGE_SM }).map((_, index) => (
                          <Grid2 key={`videos-skeleton-${index}`} size={{ xs: 12, md: 3 }}>
                              <MediaStoryCard
                                  key={`videos-skeleton-${index}`}
                                  image={{
                                      url: '/static/img/backgrounds/LeftSplitImageDesktop1.png',
                                      alt_text: intl.formatMessage({
                                          id: 'videos.loadingThumbnail',
                                          defaultMessage: 'Loading video thumbnail',
                                      }),
                                  }}
                                  date="Loading"
                                  slug="123"
                                  title="Loading"
                                  loading
                              />
                          </Grid2>
                      ))
                    : latestVideos?.results.map((video) => (
                          <Grid2
                              key={video.external_id}
                              size={{
                                  xs: 12,
                                  md: 3,
                              }}
                          >
                              <MediaStoryCard
                                  key={video.external_id}
                                  slug={video.external_id}
                                  image={{ url: video.thumbnail, alt_text: `Thumbnail for ${video.title}` }}
                                  date={video.publish_date}
                                  title={video.title}
                                  link={{
                                      url: `https://www.youtube.com/watch?v=${video.external_id}`,
                                      fn: isMdOrAbove ? () => setOpenVideo(video.external_id) : undefined,
                                      text: (
                                          <FormattedMessage
                                              id="common.actions.watchVideo"
                                              defaultMessage="Watch video"
                                          />
                                      ),
                                  }}
                              />
                          </Grid2>
                      ))}
            </Grid2>
            <Dialog
                open={!!openVideo}
                onClose={() => setOpenVideo(null)}
                sx={{ '& .MuiPaper-root': { aspectRatio: 1.7, position: 'relative', borderRadius: '2rem' } }}
            >
                <div className="h-[85%] aspect-[calc(16/9)] m-auto">
                    <button className="absolute top-6 right-6" onClick={() => setOpenVideo(null)}>
                        <CloseIcon />
                    </button>
                    <div className="h-full w-full">
                        <iframe
                            className="h-full w-full"
                            src={`https://www.youtube.com/embed/${openVideo}?autoplay=1`}
                            allow="autoplay"
                            referrerPolicy="strict-origin-when-cross-origin"
                        ></iframe>
                    </div>
                </div>
            </Dialog>
        </Box>
    ) : null;
};
